import CreateNewFolderIcon from "@mui/icons-material/CreateNewFolderOutlined";
import { Box, Button, Typography } from "@mui/material";
import type { GridRowSelectionModel } from "@mui/x-data-grid-premium";
import { useCallback, useState } from "react";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import { getRowSelectionModelFromIds, getSelectedRowIdsFromModel } from "@entrilia/shared/components/grid/gridHelper";
import useFetch from "@entrilia/shared/hooks/useFetch";
import adminApi from "../../../../../api/adminApi";
import type { DataImportFileContainer, DataImportFileContainerInfo } from "../../../../../api/types/dataImportTypes";
import { useImportCsvDataContext } from "../ImportCsvDataContext";
import type { ImportCsvTargetOption } from "../importCsvDataPageState";
import { setSelectedFileContainerAction } from "../importCsvDataPageState";
import DeleteFileContainerDialog from "./DeleteFileContainerDialog";
import FileContainerDialog from "./FileContainerDialog";
import FileContainersGrid from "./FileContainersGrid";
import { FileContainersListContextProvider } from "./FileContainersListContextProvider";

interface Props {
  targetOption: ImportCsvTargetOption;
}

interface FileContainerDialogState {
  mode: "create" | "update" | "delete";
  fileContainerInfo?: DataImportFileContainerInfo;
}

const FileContainersList = ({ targetOption }: Props) => {
  const { setPageState } = useImportCsvDataContext();

  const [fileContainerDialogState, setFileContainerDialogState] = useState<FileContainerDialogState>();

  const [selectedContainerIds, setSelectedContainerIds] = useState<string[]>(() =>
    targetOption.selectedFileContainerId ? [targetOption.selectedFileContainerId] : []
  );

  const [fileContainers, fetchError, { isFetching, fetch: fetchFileContainers }] = useFetch(
    adminApi.getDataImportFileContainers
  );

  const handleRowSelectionModelChange = useCallback(
    (selection: GridRowSelectionModel) => {
      const selectedRowIds = getSelectedRowIdsFromModel(selection);
      setSelectedContainerIds(selectedRowIds);
      setPageState(setSelectedFileContainerAction(targetOption.key, selectedRowIds[0]));
    },
    [setPageState, targetOption.key]
  );

  const handleContainerSaved = useCallback(
    async (fileContainerId: string) => {
      setFileContainerDialogState(undefined);
      await fetchFileContainers();
      setTimeout(() => handleRowSelectionModelChange({ ids: new Set([fileContainerId]), type: "include" }));
    },
    [fetchFileContainers, handleRowSelectionModelChange]
  );

  const handleContainerDeleted = useCallback(() => {
    setFileContainerDialogState(undefined);
    fetchFileContainers();
  }, [fetchFileContainers]);

  const handleUpdateContainer = useCallback((fileContainer: DataImportFileContainer) => {
    setFileContainerDialogState({
      mode: "update",
      fileContainerInfo: { id: fileContainer.id, containerName: fileContainer.name },
    });
  }, []);

  const handleDeleteContainer = useCallback((fileContainer: DataImportFileContainer) => {
    setFileContainerDialogState({
      mode: "delete",
      fileContainerInfo: { id: fileContainer.id, containerName: fileContainer.name },
    });
  }, []);

  if (fetchError) {
    return <DataLoadingFailed title="Failed to load file containers" />;
  }

  return (
    <FileContainersListContextProvider
      onUpdateFileContainer={handleUpdateContainer}
      onDeleteFileContainer={handleDeleteContainer}
    >
      <Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 2 }}>
          <Typography variant="subtitle2">File Containers</Typography>
          <Button
            disabled={isFetching}
            startIcon={<CreateNewFolderIcon />}
            onClick={() => setFileContainerDialogState({ mode: "create" })}
          >
            Add New
          </Button>
        </Box>

        <FileContainersGrid
          rows={fileContainers ?? []}
          rowSelectionModel={getRowSelectionModelFromIds(selectedContainerIds)}
          onRowSelectionModelChange={handleRowSelectionModelChange}
          loading={isFetching}
        />
      </Box>
      {(fileContainerDialogState?.mode === "create" || fileContainerDialogState?.mode === "update") && (
        <FileContainerDialog
          mode={fileContainerDialogState.mode}
          editedFileContainerInfo={fileContainerDialogState.fileContainerInfo}
          onClose={() => setFileContainerDialogState(undefined)}
          onSaved={handleContainerSaved}
        />
      )}
      {fileContainerDialogState?.mode === "delete" && fileContainerDialogState.fileContainerInfo !== undefined && (
        <DeleteFileContainerDialog
          fileContainerInfo={fileContainerDialogState.fileContainerInfo}
          onClose={() => setFileContainerDialogState(undefined)}
          onDeleted={handleContainerDeleted}
        />
      )}
    </FileContainersListContextProvider>
  );
};

export default FileContainersList;
